// One frozen task: every result on it, the session it came from opened at the task (what the candidate gets is its
// first message and the user's later messages), the reference it is graded against, the sandbox (checkouts, what it
// lacks, the outside calls recorded for playback) and the verifier's verdict. Long parts scroll in boxes of their own.
import { state, esc, get, renderMd } from '../core'
import { ghDiff, scoreBar, statusPill } from '../task-replay'
import { crumbs, wireCrumbs } from './crumbs'
import { openDataset, openResult } from './index'
import { day, duration, modelName, money, typeName } from './fmt'
import { chip, dot, onThisPage, pageHead, typeChip, wireToc } from './head'
import { modelColors } from './pareto'
import { loadSession, sessionMount } from './session'
import { prose, wireProse } from './prose'
import { wireTips } from './tip'

type Any = Record<string, any>

const cache = new Map<string, Any>()

export function renderTask(root: HTMLElement, id: string): void {
  const hit = cache.get(id)
  if (hit) { paint(root, hit); return }
  root.innerHTML = '<div class="empty">Loading the task…</div>'
  get('/api/replay/tasks/' + encodeURIComponent(id))
    .then((t: any) => {
      if (!t || t.error) throw new Error(t?.error || 'no task')
      cache.set(id, t)
      if (state.rp.task === id && !state.rp.run) paint(root, t)
    })
    .catch(() => { root.innerHTML = '<div class="empty">Could not load this task.</div>' })
}

/** A part of a section, under a small heading. */
const part = (title: string, meta: string, body: string) =>
  '<div class="rp-count-h">' + esc(title) + (meta ? ' · ' + meta : '') + '</div>' + body

const block = (title: string, sub: string, body: string, id = '') =>
  '<div class="tr-block"' + (id ? ' id="' + id + '"' : '') + '><div class="tr-block-head"><h3>' + esc(title) + '</h3>' +
  (sub ? '<span class="tr-sub">' + sub + '</span>' : '') + '</div>' + body + '</div>'

function paint(root: HTMLElement, t: Any): void {
  const sub = t.span.agent && t.span.agent !== 'main'
  const span = sub ? 'a subagent’s run' : t.span.hi !== t.span.lo ? 'turns ' + t.span.lo + '–' + t.span.hi : 'turn ' + t.span.lo
  const ds = state.rp.id
  const dsName = t.datasets.find((d: Any) => d.id === ds)?.name || 'Dataset'
  const trail = [
    { label: 'Datasets', go: () => openDataset('') },
    ...(ds ? [{ label: dsName, go: () => openDataset(ds) }, { label: 'Tasks', go: () => openDataset(ds, 'rpTasks') }] : []),
  ]
  const head = pageHead({
    trail, title: t.title || t.id,
    chips: typeChip(t.taskType) + chip(t.complexity),
    facts: [esc(t.repo), span, 'frozen ' + esc(day(t.frozenAt)), '<span class="mono">' + esc(t.id) + '</span>'],
    actions: sub ? '' : sessionLink(t.sessionId, 'Open session ↗', 'btn'),
  })
  root.innerHTML = '<section class="tr-l0 rp-task">' + head +
    onThisPage([['rpResults', 'Results'], ['rpSessionBlock', 'Session'], ['rpReference', 'Reference'], ['rpSandbox', 'Sandbox'],
      ...(t.verification ? [['rpVerification', 'Verification']] as [string, string][] : [])]) +
    block('Results', String(t.results.length), results(t), 'rpResults') +
    block('Session', t.later.length ? 'first message and ' + t.later.length + ' follow-up' + (t.later.length === 1 ? '' : 's') : 'first message', sessionMount(), 'rpSessionBlock') +
    block('Reference', esc(t.reference.kind), reference(t.reference), 'rpReference') +
    block('Sandbox', '', sandbox(t), 'rpSandbox') +
    (t.verification ? block('Verification', '', verification(t.verification), 'rpVerification') : '') +
    '</section>'
  wireCrumbs(root, trail)
  Array.prototype.forEach.call(root.querySelectorAll('tr[data-run]'), (tr: HTMLElement) => {
    tr.onclick = () => openResult(tr.getAttribute('data-run') || '', t.id, tr.getAttribute('data-model') || '')
  })
  loadSession(root, t)
  wireToc(root)
  wireProse(root)
  wireTips(root)
}

function results(t: Any): string {
  if (!t.results.length) return '<div class="empty">Not run yet.</div>'
  const colors = modelColors([...new Set(t.results.map((r: Any) => r.model as string))] as string[])
  return '<div class="tr-card tr-card-flat"><table class="tr-lb2"><thead><tr><th>Model</th><th>Score</th><th>Verdict</th><th class="r">Agent cost</th>' +
    '<th class="r">Time</th><th>Run</th><th>When</th></tr></thead><tbody>' +
    t.results.map((r: Any) => '<tr class="rp-link" data-run="' + esc(r.runId) + '" data-model="' + esc(r.model) + '"><td>' + dot(colors.get(r.model)) +
      esc(modelName(r.model)) + '<div class="tr-lb-sub">' + esc(r.agent) + '</div></td>' +
      '<td class="tr-lb-scorecell">' + (r.status === 'ok' ? scoreBar(r.score) : statusPill(r.status)) + '</td><td>' + esc(r.verdict || '') + '</td>' +
      '<td class="r">' + money(r.cost) + '</td><td class="r">' + duration(r.seconds) + '</td><td>' + esc(r.runLabel || r.runId.slice(0, 8)) + '</td>' +
      '<td class="tr-when">' + esc(day(r.createdAt)) + '</td></tr>').join('') + '</tbody></table></div>'
}

function reference(ref: Any): string {
  let out = ''
  if (ref.patch) out += part('Patch', esc(ref.diffstat || ''), '<div class="tr-card rp-scroll">' + ghDiff(ref.patch) + '</div>')
  if ((ref.claims || []).length) {
    out += part('Claims', String(ref.claims.length), '<div class="tr-card tr-card-flat rp-scroll"><table class="tr-lb2 rp-crit"><tbody>' + ref.claims.map((c: Any, i: number) =>
      '<tr><td class="rp-crit-k mono">C' + (i + 1) + '</td><td class="rp-ev">' + esc(c.claim || '') +
      (c.central ? ' <span class="rp-pill rp-pill-good">central</span>' : '') +
      ((c.citations || []).length ? '<div class="tr-muted mono">' + esc(c.citations.join(' · ')) + '</div>' : '') + '</td></tr>').join('') + '</tbody></table></div>')
  }
  if ((ref.findings || []).length) {
    out += part('Findings', String(ref.findings.length), '<div class="tr-card tr-card-flat rp-scroll"><table class="tr-lb2 rp-crit"><tbody>' + ref.findings.map((f: Any, i: number) =>
      '<tr><td class="rp-crit-k mono">R' + (i + 1) + '</td><td>' + (f.severity ? '<span class="rp-pill rp-pill-' + (f.severity === 'blocker' ? 'bad' : f.severity === 'major' ? 'mid' : 'none') + '">' +
        esc(f.severity) + '</span>' : '') + '</td><td class="rp-ev">' + esc(String(f.finding || f.title || f.summary || f.text || '')) +
      (f.where || f.file ? '<div class="tr-muted mono">' + esc(String(f.where || f.file)) + '</div>' : '') + '</td></tr>').join('') + '</tbody></table></div>')
  }
  if (ref.document) out += part('Document', '', '<div class="tr-card rp-scroll"><div class="tr-prose rp-prose rp-doc-body">' + renderMd(String(ref.document)) + '</div></div>')
  return out || '<div class="empty">No reference.</div>'
}

/** A path under someone's home folder as ~/…: where it sat on their machine says little. */
const homePath = (p: string) => p.replace(/^\/(Users|home)\/[^/]+\//, '~/')

function sandbox(t: Any): string {
  const deleted = t.checkouts.some((c: Any) => c.deleted)
  const rows = [...t.checkouts].sort((a: Any, b: Any) => Number(b.taskCheckout) - Number(a.taskCheckout)).map((c: Any) =>
    '<tr><td><b>' + esc(c.repo || '—') + '</b>' + (c.taskCheckout ? ' <span class="rp-pill rp-pill-good">task</span>' : '') +
    '<div class="tr-muted mono rp-path">' + esc(homePath(c.path)) + '</div></td>' +
    '<td class="mono">' + esc(c.branch) + '</td><td class="mono">' + esc(c.commit.slice(0, 10)) + '</td>' +
    '<td class="r">' + (c.files || '') + '</td>' + (deleted ? '<td class="r">' + (c.deleted || '') + '</td>' : '') + '</tr>').join('')
  const lacks = [
    ...t.absent.map((a: Any) => esc(a.repo) + ': ' + esc(a.why)),
    ...t.missing.map((m: Any) => esc(m.kind) + ' · ' + esc(m.root) + (m.count ? ' (' + m.count + ')' : '') + (m.status ? ' · ' + esc(m.status) : '')),
  ]
  return '<div class="tr-card tr-card-flat"><table class="tr-lb2"><thead><tr><th>Repo</th><th>Branch</th><th>Commit</th>' +
    '<th class="r" data-tip="Files the session had changed or read before the task">Files</th>' + (deleted ? '<th class="r">Deleted</th>' : '') +
    '</tr></thead><tbody>' + rows + '</tbody></table></div>' +
    (t.staged.length ? '<div class="rp-count-h">Written outside the checkouts</div><div class="rp-facts mono">' + t.staged.map((p: string) => '<span>' + esc(homePath(p)) + '</span>').join('') + '</div>' : '') +
    (lacks.length ? '<div class="rp-count-h">Not in the sandbox</div><ul class="rp-list">' + lacks.map((l) => '<li>' + l + '</li>').join('') + '</ul>' : '') +
    (t.recordings.length ? '<div class="rp-count-h">Recorded outside calls · ' + t.recordings.length + '</div>' + recordings(t.recordings) : '')
}

function recordings(list: Any[]): string {
  return '<div class="rp-recs rp-scroll">' + list.map((c) => {
    const call = c.kind.startsWith('mcp') ? c.detail + (c.call && c.call !== '{}' ? ' ' + c.call : '') : c.call
    return '<details class="rp-rec"><summary><span class="rp-pill rp-pill-none">' + esc(c.kind) + '</span>' +
      (c.turn != null ? '<span class="tr-muted"> turn ' + c.turn + '</span>' : '') + (c.error ? ' <span class="rp-pill rp-pill-bad">failed</span>' : '') +
      (c.write ? ' <span class="rp-pill rp-pill-mid">write</span>' : '') + ' <span class="mono rp-rec-call">' + esc(String(call).slice(0, 240)) + '</span></summary>' +
      '<pre class="tool-output-pre">' + esc(c.output) + '</pre></details>'
  }).join('') + '</div>'
}

/** The verifier's checks that only steer mining (where the task's span starts and ends, its type): not shown. */
const MINING_ONLY = new Set(['type_and_span'])

/** A link to the session in the Sessions view, in a new tab. */
const sessionLink = (id: string, text: string, cls = 'rp-a') =>
  '<a class="' + cls + '" href="#/sessions?session=' + encodeURIComponent(id) + '" target="_blank" rel="noopener">' + esc(text) + '</a>'

function verification(v: Any): string {
  const tone = v.verdict === 'pass' ? 'good' : v.verdict === 'fix' ? 'mid' : 'bad'
  const by = String(v.producedBy || '').match(/(us\.anthropic\.[\w.:-]+|gpt-[\w.-]+|claude-[\w.-]+)/)?.[1]
  const crit = Object.entries(v.criteria || {}).filter(([k]) => !MINING_ONLY.has(k)) as [string, string][]
  const passed = (x: string) => /^pass\b/i.test(String(x).trim())
  return '<div class="rp-checks"><span class="rp-pill rp-pill-' + tone + '">' + esc(v.verdict || '—') + '</span>' +
    crit.map(([k, x]) => '<span class="rp-check' + (passed(x) ? '' : ' fail') + '">' + (passed(x) ? '✓ ' : '✗ ') + esc(k.replace(/_/g, ' ')) + '</span>').join('') +
    (by ? '<span class="tr-muted rp-by">verified by ' + esc(modelName(by)) + '</span>' : '') + '</div>' +
    (v.summary ? '<div class="rp-ev rp-ev-block">' + prose(v.summary) + '</div>' : '') +
    crit.filter(([, x]) => !passed(x)).map(([k, x]) => '<div class="rp-count-h">' + esc(k.replace(/_/g, ' ')) + '</div><div class="rp-ev rp-ev-block">' + prose(x) + '</div>').join('')
}
